import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { User } from 'app/models/User';
import { PageTitleService } from 'app/services/page-title.service';
import { UserService } from 'app/services/user.service';
import { UtilityService } from 'app/services/utility.service';
import { availableForms, MenuItem } from 'app/shared/data/AvailableForms';
import { NavService } from 'app/services/nav.service';
import { ClientsService } from 'app/services/clients.service';
import { Client } from 'app/models/Client';
import { BehaviorSubject } from 'rxjs';
@Component({
	selector: 'app-sidebar-v2',
	templateUrl: './sidebar-v2.component.html',
	styleUrl: './sidebar-v2.component.css',
	standalone: false,
})
export class SidebarV2Component implements OnInit {
	loadingClients$ = new BehaviorSubject<boolean>(false);

	@Output() sidebarToggled = new EventEmitter<boolean>();
	sidebarCollapsed = false;
	selectedClientId: string = '';
	user: User;
	userForms: MenuItem[] = [];
	clients: Client[] = [];
	filteredForms: MenuItem[] = [];
	filterText = '';
	isMobile = false;
	defaultMenuItems: MenuItem[] = [
		// Client Admin remains at the top
		{
			id: 'client-admin',
			label: 'Client Administrator',
			children: [],
			section: 'Client Administrator',
		},
		{
			id: 'main-dashboard',
			label: 'Dashboard',
			route: 'dashboard',
			section: 'Dashboard',
		},
		{
			id: 'form-search',
			label: 'Form Search',
			route: 'pages/form-search',
			section: 'Form Search',
		},
		{
			id: 'quick-approval',
			label: 'Quick Approval',
			route: 'pages/quick-approval',
			section: 'Quick Approval',
		},
	];
	hasMultipleClients = false;

	constructor(
		private userService: UserService,
		private utilityService: UtilityService,
		public pageTitleService: PageTitleService,
		private router: Router,
		private navService: NavService, // navService is injected in order to trigger GetUserForms which is required by dashboard component (will be removed later)
		private clientService: ClientsService
	) {
		this.user = this.userService.currentUserValue.user;
	}

	get canViewAllClients(): boolean {
		return this.userService.isAdmin() || this.userService.isReadOnlyView();
	}

	ngOnInit() {
		this.checkIfMobile();

		this.selectedClientId = this.user.clientId;

		this.hasMultipleClients = (this.user.clients?.length ?? 0) > 1;
		if (this.canViewAllClients || this.hasMultipleClients) {
			this.loadingClients$.next(true);
			this.clientService.getClientList().subscribe({
				next: (data) => {
					const activeClients = data.filter((client) => client.isActive);
					this.clients = this.canViewAllClients
						? activeClients
						: activeClients.filter((client) => this.user.clients.some((c) => c.clientId === client.id));
				},
				error: (error) => {
					console.error('Error fetching clients:', error);
				},
				complete: () => {
					this.loadingClients$.next(false);
				},
			});
		} else {
			this.loadingClients$.next(true);

			this.clientService.getClient(this.user.clientId).subscribe({
				next: (client) => {
					this.clients = [client];
				},
				error: (error) => {
					console.error('Error fetching client:', error);
				},
				complete: () => {
					this.loadingClients$.next(false);
				},
			});
		}

		this.defaultMenuItems.forEach((item) => {
			if (item.id === 'client-admin') {
				if (this.user?.claims?.[this.user.clientId]?.canAddUser) {
					item.children.push({
						id: 'client-users-add',
						label: 'Add User',
						route: 'client-users/add',
						section: 'Client Administrator',
					});
				}

				if (this.user?.claims?.[this.user.clientId]?.canViewUser) {
					item.children.push({
						id: 'client-users-list',
						label: 'User List',
						route: 'client-users/list',
						section: 'Client Administrator',
					});
				}
			}
		});

		if (this.userService.isAdmin()) {
			this.defaultMenuItems.unshift({
				id: 'admin',
				label: 'Administrator',
				children: [
					{ id: 'clients-list', label: 'Client List', route: 'admin/clients/list', section: 'Administrator' },
					{ id: 'clients-add', label: 'Add Client', route: 'admin/clients/add', section: 'Administrator' },
					{ id: 'users-list', label: 'User List', route: 'admin/users/list', section: 'Administrator' },
					{ id: 'users-add', label: 'Add User', route: 'admin/users/add', section: 'Administrator' },
					{
						id: 'configuration-list',
						label: 'Configurations List',
						route: 'admin/configuration/list',
						section: 'Administrator',
					},
					{ id: 'roles', label: 'Roles', route: 'admin/roles', section: 'Administrator' },
					{
						id: 'payment-listing',
						label: 'Payments Listing Reports Update',
						route: 'admin/payment-listing',
						section: 'Administrator',
					},
					{
						id: 'workflow-forms-configuration',
						label: 'Workflow Editable Configuration',
						route: 'admin/workflow-forms-configuration',
						section: 'Administrator',
					},
					{
						id: 'administrative-roles-management',
						label: 'Administrative Roles Management',
						route: 'admin/administrative-roles-management',
						section: 'Administrator',
					},
					{ id: 'aichatlogs', label: 'AI Chat Logs', route: 'admin/aichatlogs', section: 'Administrator' },
					{ id: 'issues-report', label: 'Issues Report', route: 'pages/issues-report', section: 'Administrator' },
					{
						id: 'unclear-transactions-issues',
						label: 'Unclear Transaction Report',
						route: 'pages/unclear-transactions-issues',
						section: 'Administrator',
					},
				],
				section: 'Administrator',
			});
		}

		if (this.userService.isAclarianEmployee() || this.userService.isAdmin()) {
			this.defaultMenuItems.push({
				id: 'monthly-time-sheet',
				label: 'Monthly Time Sheet',
				route: 'forms/monthly-timesheet',
				section: 'Monthly Time Sheet',
			});
		}

		if (this.user.clients?.length > 1) {
			this.defaultMenuItems.push({
				id: '',
				label: 'Multi Client Dashboard',
				route: 'multi-client-dashboard',
				section: 'Multi Client Dashboard',
			});
		}

		const tempUserForms = availableForms.filter((form) => {
			return (
				this.userService.isAdmin() ||
				this.user?.claims[this.user?.clientId]?.claims?.some((c) => c.id == form.id && c.visible)
			);
		});

		const sortedForms = this.sortMenuItems(tempUserForms);

		this.userForms = this.defaultMenuItems.concat(sortedForms);

		this.filteredForms = [...this.userForms];

		if (this.pageTitleService.getPageTitle() === '') {
			const currRoute = this.router.url;
			const currMenuItem = this.findMenuItemByRoute(currRoute, this.userForms);
			this.pageTitleService.setPageTitle(currMenuItem?.section);
		}
	}

	// recursively sort menu items with fixed order for forms, data management, and reports
	sortMenuItems(menuItems: MenuItem[]): MenuItem[] {
		return menuItems
			.map((item) => ({
				...item,
				children: item.children ? this.sortMenuItems(item.children) : undefined,
			}))
			.sort((a, b) => {
				const labelA = a.label || '';
				const labelB = b.label || '';

				// Define the specific order for Forms, Data Management, Reports
				const fixedOrder = ['Forms', 'Data Management', 'Reports'];
				const indexA = fixedOrder.indexOf(labelA);
				const indexB = fixedOrder.indexOf(labelB);

				// If both items are in the fixed order array, sort by their position
				if (indexA !== -1 && indexB !== -1) {
					return indexA - indexB;
				}

				// If neither is in fixed order, sort alphabetically
				return labelA.localeCompare(labelB);
			});
	}

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.checkIfMobile();
	}

	checkIfMobile() {
		this.isMobile = window.innerWidth <= 768;
		if (this.isMobile) {
			this.sidebarCollapsed = true;
			this.sidebarToggled.emit(this.sidebarCollapsed);
		}
	}

	toggleSidebar() {
		this.sidebarCollapsed = !this.sidebarCollapsed;
		this.sidebarToggled.emit(this.sidebarCollapsed);
	}

	onFilterChange() {
		if (!this.filterText.trim()) {
			this.filteredForms = [...this.userForms];
			return;
		}

		const searchTerm = this.filterText.toLowerCase();

		this.filteredForms = this.userForms
			.map((mod) => {
				const filteredMod = { ...mod };

				let hasMatches = false;

				hasMatches = mod.route && mod.label.toLowerCase().includes(searchTerm);

				if (mod.children) {
					filteredMod.children = mod.children
						.map((child) => {
							const filteredChild = { ...child };

							if (child.children) {
								filteredChild.children = child.children.filter((grandchild) =>
									grandchild.label.toLowerCase().includes(searchTerm)
								);

								return filteredChild.children.length > 0 ? filteredChild : null;
							}

							return child.route && child.label?.toLowerCase().includes(searchTerm) ? filteredChild : null;
						})
						.filter((child) => child !== null);

					// Auto-expand modules and children that have matches
					if (filteredMod.children.length > 0) {
						filteredMod.expanded = true;
						filteredMod.children.forEach((child) => {
							if (child && child.children && child.children.length > 0) {
								child.expanded = true;
							}
						});
					}
				}

				return hasMatches || (filteredMod.children && filteredMod.children.length > 0) ? filteredMod : null;
			})
			.filter((mod) => mod !== null);
	}

	hasChildItems(mod) {
		return mod.children && mod.children?.length > 0;
	}

	toggleModule(module: MenuItem) {
		// Check if this is a top-level module
		const isTopLevel = this.userForms.find((mod) => mod.id === module.id);

		if (isTopLevel) {
			if (module.expanded) {
				module.expanded = false;
				// Close all its children too
				if (module.children) {
					module.children.forEach((child) => (child.expanded = false));
				}
			} else {
				// Close ALL other modules first
				this.filteredForms.forEach((mod) => {
					mod.expanded = false;
					// Close all their children too
					if (mod.children) {
						mod.children.forEach((child) => (child.expanded = false));
					}
				});

				// Then open only the clicked one
				module.expanded = true;
			}
		} else {
			// For child items, just toggle normally
			module.expanded = !module.expanded;
		}
	}

	setPageTitle(section?: string) {
		this.pageTitleService.setPageTitle(section);

		if (this.filterText?.trim()?.length > 0) {
			this.clearFilter();
		}
	}

	clearFilter() {
		this.filterText = '';
		this.onFilterChange();
	}

	clientSelected(event?: any) {
		this.userService.setClientId(
			this.selectedClientId === '' ? undefined : this.selectedClientId,
			() => {
				if (this.selectedClientId) {
					this.utilityService.retrieveConfiguration(
						this.selectedClientId,
						() => {
							location.reload();
						},
						true
					);
				} else {
					location.reload();
				}
			},
			true
		);
	}

	private findMenuItemByRoute(url: string, items: MenuItem[]): MenuItem | null {
		const cleanUrl = url.split('?')[0];

		for (const item of items) {
			// Check if this item has a route and it matches
			if (item.route) {
				// Direct match or URL contains the route
				if (cleanUrl === item.route || cleanUrl.includes(item.route)) {
					return item;
				}
			}

			// If item has children, search recursively
			if (item.children && item.children.length > 0) {
				const found = this.findMenuItemByRoute(url, item.children);
				if (found) {
					return found;
				}
			}
		}

		return null;
	}
}
